import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Stack,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { SERVER_URL } from "../../../constants";
import { can } from "../../shared utils/auth";
import AdultStays from "./AdultStays";

function GroupMemberships({ selectedPerson }) {
  // What the adult is charged is the back office's: the server nulls it out
  // and ignores it on save for anyone without FINANCE, so don't show the column.
  const showFinancials = can("finance");
  const [memberships, setMemberships] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [nsrs, setNsrs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [error, setError] = useState("");

  // Inline editing: { [bookingId]: { participationType?, actualCharge? } }
  const [editedFields, setEditedFields] = useState({});
  const [savingBookingId, setSavingBookingId] = useState(null);

  // Add product dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [eligibleProducts, setEligibleProducts] = useState([]);
  const [eligibleLoading, setEligibleLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState({}); // { [pid]: { checked, participationType } }
  const [addingProducts, setAddingProducts] = useState(false);

  const PARTICIPATION_TYPES = ["LEAD", "SUPPORT", "SUPERVISE_ONLY", "TRAVEL_ONLY", "STANDBY", "BOOKING"];

  const isResidentialType = (t) => {
    if (t == null) return false;
    return t === 0 || t === "0" || (typeof t === "string" && t.toUpperCase() === "RESIDENTIAL");
  };

  const token =
    typeof window !== "undefined" ? sessionStorage.getItem("bearer") : null;

  const adultId = selectedPerson?.id;

  const fetchMemberships = useCallback(async () => {
    if (!adultId || !token) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `${SERVER_URL}api/adult/${adultId}/group-memberships`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.status === 204) {
        setMemberships([]);
        return;
      }
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setMemberships(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to fetch group memberships", e);
      setError("Failed to load group memberships.");
    } finally {
      setLoading(false);
    }
  }, [adultId, token]);

  const fetchBookings = useCallback(async () => {
    if (!adultId || !token) return;
    setBookingsLoading(true);
    try {
      const res = await fetch(
        `${SERVER_URL}api/adults/${adultId}/adult-bookings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.status === 204) {
        setBookings([]);
        return;
      }
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setBookings(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to fetch adult bookings", e);
      setBookings([]);
    } finally {
      setBookingsLoading(false);
    }
  }, [adultId, token]);

  const fetchNsrs = useCallback(async () => {
    if (!adultId || !token) return;
    try {
      const res = await fetch(
        `${SERVER_URL}api/nsrs/${adultId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.status === 204) { setNsrs([]); return; }
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setNsrs(Array.isArray(data) ? data : data ? [data] : []);
    } catch (e) {
      console.error("Failed to fetch NSRs", e);
      setNsrs([]);
    }
  }, [adultId, token]);

  useEffect(() => {
    fetchMemberships();
    fetchBookings();
    fetchNsrs();
  }, [fetchMemberships, fetchBookings, fetchNsrs]);

  const formatDate = (d) => {
    if (!d) return "—";
    const parts = d.slice(0, 10).split("-");
    if (parts.length !== 3) return d;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const statusColor = (status) => {
    switch ((status || "").toUpperCase()) {
      case "ACTIVE": return "success";
      case "COMPLETED": return "default";
      case "CANCELLED": return "error";
      default: return "default";
    }
  };

  // --- Booking helpers ---

  const bookingProductName = (b) =>
    b.productName || b.product?.productName || b.product?.name || `Product ${b.productId || b.product_id || "?"}`;

  const bookingProductId = (b) =>
    b.productId ?? b.product_id ?? b.product?.id ?? b.product?.productId ?? null;

  const bookingId = (b) => b.id ?? b.adultBookingId ?? null;

  const bookingDates = (b) => {
    const start = b.customStart || b.custom_start || b.startDate || b.product?.startDate;
    const end = b.customEnd || b.custom_end || b.endDate || b.product?.endDate;
    return { start, end };
  };

  const handleDeleteBooking = async (booking) => {
    const bid = bookingId(booking);
    if (!bid || !token) return;
    if (!window.confirm(`Delete booking for ${bookingProductName(booking)}?`)) return;
    try {
      const res = await fetch(`${SERVER_URL}api/adult-bookings/${bid}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok && res.status !== 204) throw new Error(`${res.status}`);
      await fetchBookings();
    } catch (e) {
      console.error("Failed to delete adult booking", e);
    }
  };

  const handleSaveBooking = async (booking) => {
    const bid = bookingId(booking);
    const edits = editedFields[bid];
    if (!bid || !edits || !token) return;

    setSavingBookingId(bid);
    try {
      const res = await fetch(`${SERVER_URL}api/adult-bookings/${bid}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          participationType: edits.participationType ?? booking.participationType ?? "BOOKING",
          countsForTransport: booking.countsForTransport ?? true,
          countsForRatio: booking.countsForRatio ?? false,
          notes: booking.notes ?? "",
          customStart: booking.customStart || null,
          customEnd: booking.customEnd || null,
          // Omitted without FINANCE: the server keeps the stored charge.
          ...(showFinancials
            ? {
                actualCharge: edits.actualCharge != null
                  ? Number(edits.actualCharge)
                  : (booking.actualCharge ?? 0),
              }
            : {}),
        }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setEditedFields((prev) => {
        const next = { ...prev };
        delete next[bid];
        return next;
      });
      await fetchBookings();
    } catch (e) {
      console.error("Failed to update adult booking", e);
    } finally {
      setSavingBookingId(null);
    }
  };

  // --- Add products dialog ---

  const openAddDialog = async () => {
    setAddDialogOpen(true);
    setSelectedProducts({});
    setEligibleProducts([]);
    setEligibleLoading(true);

    try {
      // Fetch eligible products for all NSRs and merge
      const allProducts = [];
      const seenIds = new Set();

      for (const nsr of nsrs) {
        const nsrId = nsr.id;
        if (!nsrId) continue;
        try {
          const res = await fetch(
            `${SERVER_URL}api/eligableProductsWide/nsr/${nsrId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (!res.ok) continue;
          const data = await res.json();
          const products = data?.products || data || [];
          for (const p of (Array.isArray(products) ? products : [])) {
            const pid = p.id || p.productId;
            if (pid && !seenIds.has(pid)) {
              seenIds.add(pid);
              allProducts.push(p);
            }
          }
        } catch (e) {
          console.warn(`Failed to fetch eligible products for NSR ${nsrId}`, e);
        }
      }

      // Filter out products already booked and AFTERNOON types
      const bookedProductIds = new Set(bookings.map(bookingProductId).filter(Boolean));
      const available = allProducts.filter((p) => {
        if (bookedProductIds.has(p.id || p.productId)) return false;
        const t = p.productType;
        if (t === 1 || t === "1" || (typeof t === "string" && t.toUpperCase() === "AFTERNOON")) return false;
        return true;
      });

      setEligibleProducts(available);
    } catch (e) {
      console.error("Failed to load eligible products", e);
    } finally {
      setEligibleLoading(false);
    }
  };

  const toggleProduct = (pid, productType) => {
    setSelectedProducts((prev) => {
      const existing = prev[pid];
      if (existing?.checked) {
        return { ...prev, [pid]: { ...existing, checked: false } };
      }
      const defaultPart = isResidentialType(productType) ? "BOOKING" : "LEAD";
      return { ...prev, [pid]: { checked: true, participationType: existing?.participationType || defaultPart } };
    });
  };

  const setParticipationType = (pid, value) => {
    setSelectedProducts((prev) => ({
      ...prev,
      [pid]: { ...(prev[pid] || {}), participationType: value },
    }));
  };

  const selectedCount = Object.values(selectedProducts).filter((v) => v?.checked).length;

  const handleAddProducts = async () => {
    const selected = Object.entries(selectedProducts)
      .filter(([, v]) => v?.checked)
      .map(([k, v]) => ({ productId: Number(k), participationType: v.participationType }));
    if (!selected.length) return;

    setAddingProducts(true);
    try {
      for (const { productId, participationType } of selected) {
        await fetch(`${SERVER_URL}api/products/${productId}/adult-bookings`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            adultId,
            participationType,
            countsForTransport: true,
            countsForRatio: false,
            notes: "",
          }),
        });
      }
      setAddDialogOpen(false);
      await fetchBookings();
    } catch (e) {
      console.error("Failed to add adult bookings", e);
    } finally {
      setAddingProducts(false);
    }
  };

  // --- Product type grouping ---

  const productTypeLabel = (t) => {
    if (t == null) return "Other";
    const n = Number(t);
    if (n === 0 || (typeof t === "string" && t.toUpperCase() === "RESIDENTIAL")) return "Residential";
    if (n === 2 || (typeof t === "string" && t.toUpperCase() === "WEEKEND")) return "Weekend excursions";
    if (n === 3 || (typeof t === "string" && t.toUpperCase() === "EXTERNAL")) return "External activities";
    if (n === 4 || (typeof t === "string" && t.toUpperCase() === "TRANSFER")) return "Transfers";
    const s = String(t);
    if (s.toUpperCase() === "INSURANCE") return "Insurance";
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  };

  const groupByType = (products) => {
    const groups = {};
    for (const p of products) {
      const label = productTypeLabel(p.productType);
      if (!groups[label]) groups[label] = [];
      groups[label].push(p);
    }
    // Sort groups in a sensible order
    const order = ["Residential", "Weekend excursions", "External activities", "Transfers", "Insurance"];
    const sorted = [];
    for (const key of order) {
      if (groups[key]) sorted.push([key, groups[key]]);
    }
    // Add any remaining
    for (const [key, items] of Object.entries(groups)) {
      if (!order.includes(key)) sorted.push([key, items]);
    }
    return sorted;
  };

  // --- Render ---

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* GROUP MEMBERSHIPS */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">Groups</Typography>
      </Box>

      {memberships.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          This adult is not currently a member of any groups.
        </Typography>
      ) : (
        <div className="detail-card payments-card" style={{ padding: "20px 0" }}>
          <table style={{ width: "80%", textAlign: "left", margin: "auto", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th>Group</th>
                <th>Campus</th>
                <th>Type</th>
                <th>Start</th>
                <th>End</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {memberships.map((m) => (
                <tr key={m.assignmentId || m.campgroupId}>
                  <td>{m.groupName}</td>
                  <td>{m.campus || "—"}</td>
                  <td>{m.groupType || "—"}</td>
                  <td>{formatDate(m.startDate)}</td>
                  <td>{formatDate(m.endDate)}</td>
                  <td>
                    <Chip
                      label={m.status || "Unknown"}
                      size="small"
                      color={statusColor(m.status)}
                      variant="outlined"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* STAYS & TRANSFERS */}
      <Divider sx={{ my: 3 }} />
      <AdultStays selectedPerson={selectedPerson} />

      {/* BOOKINGS */}
      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">Bookings</Typography>
        <Button
          startIcon={<AddCircleOutlineIcon />}
          variant="outlined"
          size="small"
          onClick={openAddDialog}
          disabled={nsrs.length === 0}
        >
          Add product
        </Button>
      </Box>

      {nsrs.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No residential stay records found. Products can only be added when the adult has an NSR.
        </Typography>
      )}

      {bookingsLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={18} /> <span>Loading bookings…</span>
        </Box>
      ) : bookings.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No bookings for this adult.
        </Typography>
      ) : (
        <div className="detail-card payments-card" style={{ padding: "20px 0" }}>
          <table style={{ width: "80%", textAlign: "left", margin: "auto", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th>Product</th>
                <th>Start</th>
                <th>End</th>
                <th>Participation</th>
                {showFinancials && <th style={{ textAlign: "right" }}>Charge</th>}
                <th />
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => {
                const bid = bookingId(b);
                const edits = editedFields[bid] || {};
                const currentType = b.participationType || b.participation_type || "";
                const currentCharge = b.actualCharge ?? 0;
                const displayType = edits.participationType ?? currentType;
                const displayCharge = edits.actualCharge != null ? edits.actualCharge : currentCharge;

                const isDirty =
                  (edits.participationType && edits.participationType !== currentType) ||
                  (edits.actualCharge != null && Number(edits.actualCharge) !== Number(currentCharge));
                const isSaving = savingBookingId === bid;

                return (
                  <tr key={bid}>
                    <td>{b.productName || bookingProductName(b)}</td>
                    <td>{formatDate(b.customStart)}</td>
                    <td>{formatDate(b.customEnd)}</td>
                    <td>
                      {currentType === "BOOKING" ? (
                        <span style={{ color: "#888" }}>Booking</span>
                      ) : (
                        <Select
                          size="small"
                          value={displayType}
                          onChange={(e) => setEditedFields((prev) => ({
                            ...prev,
                            [bid]: { ...(prev[bid] || {}), participationType: e.target.value },
                          }))}
                          sx={{ minWidth: 150 }}
                        >
                          {PARTICIPATION_TYPES.filter((t) => t !== "BOOKING").map((t) => (
                            <MenuItem key={t} value={t}>
                              {t.replace(/_/g, " ").charAt(0) + t.replace(/_/g, " ").slice(1).toLowerCase()}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    </td>
                    {showFinancials && (
                    <td style={{ textAlign: "right" }}>
                      <TextField
                        size="small"
                        type="number"
                        value={displayCharge}
                        onChange={(e) => setEditedFields((prev) => ({
                          ...prev,
                          [bid]: { ...(prev[bid] || {}), actualCharge: e.target.value },
                        }))}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">&pound;</InputAdornment>,
                        }}
                        inputProps={{ min: 0, step: "0.01" }}
                        sx={{ width: 110 }}
                      />
                    </td>
                    )}
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {isDirty && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleSaveBooking(b)}
                          disabled={isSaving}
                          sx={{ mr: 1 }}
                        >
                          {isSaving ? "Saving…" : "Save"}
                        </Button>
                      )}
                      <IconButton size="small" onClick={() => handleDeleteBooking(b)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ADD PRODUCT DIALOG */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add product</DialogTitle>
        <DialogContent>
          {eligibleLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          ) : eligibleProducts.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No additional eligible products found.
            </Typography>
          ) : (
            <Stack sx={{ mt: 1 }} spacing={2}>
              {groupByType(eligibleProducts).map(([typeLabel, products]) => (
                <Box key={typeLabel}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {typeLabel}
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  {products.map((p) => {
                    const pid = p.id || p.productId;
                    const name = p.productName || p.name || `Product ${pid}`;
                    const dates = p.startDate
                      ? ` (${formatDate(p.startDate)} – ${formatDate(p.endDate)})`
                      : "";
                    const rate = p.defaultCharge != null
                      ? ` — ${Number(p.defaultCharge).toFixed(2)}`
                      : "";
                    const isRes = isResidentialType(p.productType);
                    const sel = selectedProducts[pid];
                    const isChecked = !!sel?.checked;
                    return (
                      <Box key={pid} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isChecked}
                              onChange={() => toggleProduct(pid, p.productType)}
                              disabled={addingProducts}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {name}{dates}{rate}
                            </Typography>
                          }
                          sx={{ flex: 1 }}
                        />
                        {isChecked && (
                          isRes ? (
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                              Booking
                            </Typography>
                          ) : (
                            <Select
                              size="small"
                              value={sel?.participationType || "LEAD"}
                              onChange={(e) => setParticipationType(pid, e.target.value)}
                              disabled={addingProducts}
                              sx={{ minWidth: 130 }}
                            >
                              {PARTICIPATION_TYPES.filter((t) => t !== "BOOKING").map((t) => (
                                <MenuItem key={t} value={t}>
                                  {t.replace(/_/g, " ").charAt(0) + t.replace(/_/g, " ").slice(1).toLowerCase()}
                                </MenuItem>
                              ))}
                            </Select>
                          )
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={addingProducts}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddProducts}
            disabled={selectedCount === 0 || addingProducts}
          >
            {addingProducts ? "Adding…" : `Add ${selectedCount || ""} product${selectedCount === 1 ? "" : "s"}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default GroupMemberships;
